/** @format */

import { Component, OnDestroy } from "@angular/core"
import { ActivatedRoute, Router } from "@angular/router"
import { Company } from "src/app/models/company.model"
import { Plant } from "src/app/models/plant.model"
import { User } from "src/app/models/user.model"
import { AuthService } from "src/app/services/auth/auth.service"
import { CompanyService } from "src/app/services/company/company.service"
import { ErrorHandlerService } from "src/app/services/errorHandler/error-handler.service"
import { PlantService } from "src/app/services/plant/plant.service"
import { ToastService } from "src/app/services/toast/toast.service"
import { UserService } from "src/app/services/user/user.service"
import { forkJoin } from "rxjs"

import constants from "src/app/constants"
import { TempUser } from "src/app/models/tempuser.model"
import { Roles, normalizeRole } from "src/app/auth/roles";
import { RefreshStateService } from "src/app/services/refresh-state/refresh-state.service"

@Component({
	standalone: false,
	selector: 'app-user',
	templateUrl: './user.component.html',
	styleUrls: ['./user.component.css'],
})
export class UserComponent implements OnDestroy {
	readonly Roles = Roles;
	constructor(
		private route: ActivatedRoute,
		private plantService: PlantService,
		private companyService: CompanyService,
		private router: Router,
		public toastService: ToastService,
		private userService: UserService,
		private authService: AuthService,
		private errorService: ErrorHandlerService,
		private refreshState: RefreshStateService,
	) { }

	activeItem = 0
	activeUserMenuKey = ''
	searchInput = ''
	showMenu = false
	showAddMenu = false
	user: User[] = [];
	tempUser: TempUser[] = [];
	assignedPlants: any = []
	errorMessage = {
		success: true,
		message: '',
	}
	deactivationRole: string = "";

	currentPage: number = 1
	pageSize = 7
	totalPages = 10
	loopArray = new Array(this.pageSize).fill(0).map((_, index) => index + 1)

	currentIncrement = this.pageSize
	userTypes = [
		{
			id: 1,
			type: "System Users"
		},
		{
			id: 2,
			type: "Temporary Users"
		}
	]

	selectedUserType = {
		id: 1,
		type: "System Users"
	}

	defaultUserRoleOption = {
		id: 0,
		role: 'All Roles',
	}

	userRoleOptions = [{ ...this.defaultUserRoleOption }]

	selectedUserRole = {
		...this.defaultUserRoleOption,
	}

	usersToDisplay: any = [];

	onSearchChange(search: any) {
		this.searchInput = search
		this.persistRefreshState()
		this.retrieveUnitsByPlantId(this.selectedPlant)
	}

	filterUsersByRole(users: User[]): User[] {
		if (this.selectedUserType.id !== 1 || this.selectedUserRole.id === 0) {
			return users
		}

		return users.filter((user: User) => user.role === this.selectedUserRole.role)
	}

	normalizeSearch(search: any) {
		return String(search || '').trim().replace(/\s+/g, ' ')
	}

	getSearchTokens(search: any) {
		return this.normalizeSearch(search).toLowerCase().split(' ').filter(Boolean)
	}

	getBackendSearch(search: any) {
		return this.getSearchTokens(search)[0] || ''
	}

	matchesSearch(user: User | TempUser, search: any) {
		const tokens = this.getSearchTokens(search)

		if (tokens.length === 0) {
			return true
		}

		const searchableText = [
			user.firstName,
			user.lastName,
			user.email,
			user.role,
		]
			.filter(Boolean)
			.join(' ')
			.toLowerCase()

		return tokens.every((token) => searchableText.includes(token))
	}

	updateUserRoleOptions(users: User[]) {
		const uniqueRoles = Array.from(
			new Set(
				users
					.map((user: User) => user.role?.trim())
					.filter((role): role is string => !!role)
			)
		).sort((roleA, roleB) => roleA.localeCompare(roleB))

		const roleOptions = uniqueRoles.map((role, index) => ({
			id: index + 1,
			role,
		}))

		if (
			this.selectedUserRole.id !== 0 &&
			!roleOptions.some((option) => option.role === this.selectedUserRole.role)
		) {
			roleOptions.push(this.selectedUserRole)
		}

		this.userRoleOptions = [
			{ ...this.defaultUserRoleOption },
			...roleOptions,
		]

		if (this.selectedUserRole.id !== 0) {
			const selectedRole = this.userRoleOptions.find(
				(option) => option.role === this.selectedUserRole.role
			)
			this.selectedUserRole = selectedRole || { ...this.defaultUserRoleOption }
		}
	}

	updatePagination(totalPages: number) {
		this.totalPages = totalPages
		this.loopArray = new Array(this.totalPages)
			.fill(0)
			.map((_, index) => index + 1)
	}

	setFilteredUsers(users: Array<User | TempUser>) {
		const searchFilteredUsers = users.filter((user) => this.matchesSearch(user, this.searchInput))
		const filteredUsers = this.selectedUserType.id === 1
			? this.filterUsersByRole(searchFilteredUsers as User[])
			: searchFilteredUsers
		const filteredTotalPages = Math.ceil(filteredUsers.length / this.pageSize)
		this.updatePagination(filteredTotalPages)

		if (this.currentPage > this.totalPages) {
			this.currentPage = this.totalPages || 1
		}

		const startIndex = (this.currentPage - 1) * this.pageSize
		this.usersToDisplay = filteredUsers.slice(startIndex, startIndex + this.pageSize)
	}

	retrieveAllUserPages(
		item: any,
		retrieveUsers: (request: any) => any,
		handleUsers: (users: any[]) => void,
	) {
		const firstPageRequest = {
			...item,
			pageNumber: 1,
		}

		retrieveUsers(firstPageRequest).subscribe({
			next: (response: any) => {
				const totalBackendPages = response.item2 || 0
				const firstPageUsers = response.item1 || []

				if (totalBackendPages <= 1) {
					handleUsers(firstPageUsers)
					return
				}

				const remainingPageRequests = Array.from(
					{ length: totalBackendPages - 1 },
					(_, index) =>
						retrieveUsers({
							...item,
							pageNumber: index + 2,
						})
				)

				forkJoin(remainingPageRequests).subscribe({
					next: (responses: any[]) => {
						handleUsers([
							...firstPageUsers,
							...responses.flatMap((pageResponse: any) => pageResponse.item1 || []),
						])
					},
					error: (response) => {
						this.errorMessage =
							this.errorService.handleRetrievalErrors(response)
					},
				})
			},
			error: (response: any) => {
				this.errorMessage =
					this.errorService.handleRetrievalErrors(response)
			},
		})
	}

	retrieveSystemUsers(item: any) {
		this.retrieveAllUserPages(
			item,
			(request: any) => this.userService.retrieveUsers(request),
			(users: User[]) => {
				this.user = users
				this.updateUserRoleOptions(this.user)
				this.setFilteredUsers(this.user)
			},
		)
	}

	retrieveMultiWordTempUsers(item: any) {
		this.retrieveAllUserPages(
			item,
			(request: any) => this.userService.retrieveTempUsers(request),
			(users: TempUser[]) => {
				this.tempUser = users
				this.setFilteredUsers(this.tempUser)
			},
		)
	}

	incrementArray(arr: any, increment: any) {
		return arr.map((item: any) => item + increment)
	}

	decrementArray(arr: any, decrement: any) {
		return arr.map((item: any) => item - decrement)
	}

	performIncrement() {
		if (this.currentPage === this.totalPages) return
		const result = this.currentPage + 1
		this.setCurrentPage(result)
		if (this.currentPage > this.loopArray[this.loopArray.length - 1]) {
			this.shiftPageRight()
		}
	}

	performDecrement() {
		if (this.currentPage === 1) return
		const result = this.currentPage - 1
		this.setCurrentPage(result)
		if (this.currentPage < this.loopArray[0]) {
			this.shiftPageLeft()
		}
	}

	removeNumbersGreaterThan(arr: any, x: any) {
		return arr.filter((number: any) => number <= x)
	}

	shiftPageRight() {
		const incrementedArray = this.incrementArray(
			this.loopArray,
			this.currentIncrement,
		)
		this.loopArray = incrementedArray
	}

	shiftPageLeft() {
		const decrementArray = this.decrementArray(
			this.loopArray,
			this.currentIncrement,
		)
		this.loopArray = decrementArray
	}

	editUserByRole(userRole: any, userTypeId = this.selectedUserType.id) {
		if (userTypeId === 2) {
			return 'edit-temporary-user'
		}

		const normalizedRole = normalizeRole(userRole)
		if (normalizedRole === Roles.operator) {
			return 'edit-non-admin-user'
		} else if (normalizedRole === Roles.admin) {
			return 'edit-admin-user'
		}
		return ''
	}

	canEditUser(data: any) {
		return this.editUserByRole(data?.role) !== ''
	}

	getEditMode(role: any) {
		if (this.selectedUserType.id === 2) {
			return 'temp-user'
		}

		return normalizeRole(role) === Roles.operator ? 'non-admin' : ''
	}

	transferData(data: any, role: any) {
		let path = this.editUserByRole(role)
		if (!path) {
			this.showMenu = false
			this.activeUserMenuKey = ''
			return
		}

		this.router.navigateByUrl(`settings/user/${path}`, {
			state: {
				selectedValues: {
					company: this.selectedCompany,
					plant: this.selectedPlant,
					userType: this.selectedUserType,
					userRole: this.selectedUserRole,
				},
				data: data,
				mode: this.getEditMode(role)
			},
		})
	}

	// selectedValues = {
	// 	company: {
	// 		companyId: 0,
	// 		companyName: "",
	// 		isDeleted: false,
	// 	},
	// 	plant: {
	// 		plantId: 0,
	// 		plantName: "",
	// 		isDeleted: false,
	// 	},
	// }

	selectedCompany = {
		companyId: 0,
		companyName: '',
		isDeleted: false,
	}
	selectedPlant = {
		plantId: 0,
		plantName: '',
		isDeleted: false,
	}

	plantDetails: Plant = {
		plantId: 0,
		plantName: '',
		isDeleted: false,
	}

	companies: Company[] = []
	plants: Plant[] = []
	currentRole: any = ''

	resetPlant = false
	firstRender = false

	selectedPage = {
		backgroundColor: '#0F2642',
		color: 'white',
		borderRadius: '4px',
	}

	toggleAdd() {
		this.showAddMenu = !this.showAddMenu
	}

	setCurrentPage(page: any) {
		this.currentPage = page
		this.persistRefreshState()
		this.retrieveUnitsByPlantId(this.selectedPlant, true)
		// call user endpoint with new page and set this.user
	}

	showPlantEmptyState() {
		if (
			this.currentRole === Roles.superAdmin &&
			this.selectedCompany.companyId > 0 &&
			(this.plants.length === 0 || this.selectedPlant.plantId === 0)
		) {
			return true
		} else if (
			this.currentRole === Roles.admin &&
			(this.plants.length === 0 || this.selectedPlant.plantId === 0)
		) {
			return true
		} else {
			return false
		}
	}

	retrievePlantsByCompanyId(selected: any) {
		if (!this.firstRender) {
			this.selectedCompany = selected

			if (this.selectedPlant.plantId > 0 || this.plants.length > 0) {
				this.resetPlant = true
				this.selectedPlant = {
					plantId: 0,
					plantName: '',
					isDeleted: false,
				}

				this.user = []
				this.plants = []
			}
			if (
				this.selectedCompany.companyId !== 0 &&
				this.selectedPlant.plantId === 0
			) {
				this.user = []
			}
		}
		this.plantService
			.retrieveCompanyPlants(this.selectedCompany.companyId.toString())
			.subscribe({
				next: (response) => {
					this.plants = response
				},
				error: (err) => {
					this.errorMessage =
						this.errorService.handleRetrievalErrors(err)
				},
			})
		this.resetPlant = false
		this.persistRefreshState()
	}

	specialStyle = {
		flex: 0.5,
	}

	defaultStyle = {
		flex: 1,
	}
	activateSubmitBtn() {
		if (this.currentRole === Roles.superAdmin) {
			if (
				this.selectedCompany.companyId === 0 ||
				this.selectedPlant.plantId === 0
			) {
				return false
			} else if (
				this.selectedCompany.isDeleted ||
				this.selectedPlant.isDeleted
			) {
				return false
			} else {
				return true
			}
		} else if (this.currentRole === Roles.admin) {
			if (this.selectedPlant.plantId === 0) {
				return false
			} else if (this.selectedPlant.isDeleted) {
				return false
			} else {
				return true
			}
		}
		return true
	}

	closeAddButton() {
		this.showAddMenu = false
	}
	retrieveUnitsByPlantId(selected: any, reset?: any) {
		if (reset !== true) {
			this.currentPage = 1
		}

		this.selectedPlant = selected
		this.persistRefreshState()
		const plantId = this.selectedPlant.plantId
		const pageNumber = this.currentPage
		const pageSize = this.pageSize

		// here
		const item = {
			pageSize,
			pageNumber,
			search: this.getBackendSearch(this.searchInput),
			plantId,
		}
		const isMultiWordSearch = this.getSearchTokens(this.searchInput).length > 1

		if (this.selectedUserType.id == 1) {
			this.retrieveSystemUsers(item)
		} else {
			if (isMultiWordSearch) {
				this.retrieveMultiWordTempUsers(item)
				return
			}

			this.userService.retrieveTempUsers(item).subscribe({
				next: (response: any) => {
					this.user = [];
					this.tempUser = response.item1
					this.usersToDisplay = this.tempUser
					this.updatePagination(response.item2)
				},
				error: (response) => {
					this.errorMessage =
						this.errorService.handleRetrievalErrors(response)
				},
			});
		}
	}

	handleSystemType(selected: any, reset?: any) {
		if (selected.id != this.selectedUserType.id) {
			this.selectedUserType = selected;
		}

		if (this.selectedUserType.id !== 1) {
			this.selectedUserRole = { ...this.defaultUserRoleOption }
			this.userRoleOptions = [{ ...this.defaultUserRoleOption }]
		}

		this.retrieveUnitsByPlantId(this.selectedPlant);
		this.persistRefreshState()
	}

	handleUserRole(selected: any) {
		if (selected.id === this.selectedUserRole.id) {
			return
		}

		this.selectedUserRole = selected
		this.persistRefreshState()
		this.retrieveUnitsByPlantId(this.selectedPlant)
	}

	adminColor = {
		backgroundColor: '#F49025',
	}

	superAdminColor = {
		backgroundColor: '#069A51',
	}

	nonAdminColor = {
		backgroundColor: '#102F53',
	}

	getRoleColor(role: any) {
		if (role === Roles.admin) {
			return this.adminColor
		}

		if (role === Roles.superAdmin) {
			return this.superAdminColor
		}

		return this.nonAdminColor
	}

	showToast: boolean = false
	toastText: string = ''

	getUserMenuKey(data: User | TempUser, rowIndex: number) {
		const userId = this.selectedUserType.id === 1
			? (data as User).userId
			: (data as TempUser).tempUserId
		const fallbackId = data.email || `${data.firstName}-${data.lastName}`

		return `${this.selectedUserType.id}:${userId ?? fallbackId}:${rowIndex}`
	}

	isUserMenuOpen(data: User | TempUser, rowIndex: number) {
		return this.showMenu && this.activeUserMenuKey === this.getUserMenuKey(data, rowIndex)
	}

	toggleMenu(data: User | TempUser, rowIndex: number) {
		const menuKey = this.getUserMenuKey(data, rowIndex)
		this.activeItem = this.selectedUserType.id === 1
			? (data as User).userId
			: (data as TempUser).tempUserId

		if (this.showMenu && this.activeUserMenuKey === menuKey) {
			this.showMenu = false
			this.activeUserMenuKey = ''
			return
		}

		this.activeUserMenuKey = menuKey
		this.showMenu = true
	}

	handleSuccessToast(state: any) {
		if (state === constants.addAdminUser)
			this.toastService.toastText = 'Admin User successfully added'

		if (state === constants.addSuperUser)
			this.toastService.toastText = 'Super Admin User successfully added!'

		if (state === constants.addNonAdminUser /*"Add-Non-Admin-User"*/)
			this.toastService.toastText = 'Non Admin User successfully added!'

		if (state === constants.editAdminUser /*"Edit-Admin-User"*/)
			this.toastService.toastText = 'Admin User successfully updated!'

		if (state === constants.editNonAdminUser /*"Edit-Non-Admin-User"*/)
			this.toastService.toastText = 'Non-Admin User successfully updated!'

		if (state === constants.editSuperAdminUser)
			this.toastService.toastText =
				'Super Admin User successfully updated'

		if (state === constants.userDeactivated /*"User-Deactivated"*/) {
			this.toastService.toastText = 'User successfully deactivated!'
		}

		if (state === constants.tempUserDeactivated) {
			this.toastService.toastText = 'Temporary User successfully deactivated!'
		}

		this.toastService.showToast = state || false
		setTimeout(() => {
			this.toastService.showToast = false
		}, 5000)
	}

	pageDetail = ''
	ngOnInit(): void {
		this.currentRole = localStorage.getItem('role')
		const saved = this.refreshState.load<{
			selectedCompany: any;
			selectedPlant: any;
			selectedUserType: any;
			selectedUserRole: any;
			searchInput: string;
			currentPage: number;
		}>('settings:users')

		this.pageDetail = history.state[2]
		this.firstRender = history.state.firstRender || Boolean(saved?.selectedPlant?.plantId || saved?.selectedCompany?.companyId)
		if (!history.state.firstRender && saved) {
			this.selectedCompany = saved.selectedCompany || this.selectedCompany
			this.selectedPlant = saved.selectedPlant || this.selectedPlant
			this.selectedUserType = saved.selectedUserType || this.selectedUserType
			this.selectedUserRole = saved.selectedUserRole || this.selectedUserRole
			this.searchInput = saved.searchInput || ''
			this.currentPage = Math.max(1, saved.currentPage || 1)
		}
		if (this.firstRender == true) {
			if (history.state.firstRender) {
				this.selectedCompany = { ...history?.state?.selectedValues?.company }
				this.selectedPlant = { ...history?.state?.selectedValues?.plant }
			}
			if (history?.state?.selectedValues?.userType) {
				this.selectedUserType = { ...history?.state?.selectedValues?.userType }
			}
			if (history?.state?.selectedValues?.userRole) {
				this.selectedUserRole = { ...history?.state?.selectedValues?.userRole }
			}
			this.retrievePlantsByCompanyId(this.selectedCompany)
			this.retrieveUnitsByPlantId(this.selectedPlant)
			this.firstRender = false
		}
		try {
			const storedAssignment = localStorage.getItem('assignedPlants')
			if (storedAssignment) {
				this.assignedPlants = JSON.parse(storedAssignment)
			} else {
				console.error(
					"No data found in local storage for key 'assignedPlants' ",
				)
			}
		} catch (error) {
			console.error('Error parsing JSON data from local storage:', error)
		}

		this.handleSuccessToast(history.state.prevPage)

		if (this.currentRole === Roles.superAdmin) {
			this.companyService.retrieveCompanies().subscribe({
				next: (companies) => {
					this.companies = companies
				},
				error: (response) => {
					this.errorMessage =
						this.errorService.handleRetrievalErrors(response)
				},
			})
		}

		// cancel button
		if (this.pageDetail === 'Add-Non-Admin-User') {
			if (this.currentRole === Roles.admin) {
				this.assignedPlants.map((assignment: Plant) =>
					this.plants.push(assignment),
				)
			} else {
				this.retrievePlantsByCompanyId(history.state[0])
			}
			this.retrieveUnitsByPlantId(history.state[1])
		}

		// update button
		if (history.state.details) {
			if (this.currentRole === Roles.admin) {
				this.assignedPlants.map((assignment: Plant) =>
					this.plants.push(assignment),
				)
			} else {
				this.retrievePlantsByCompanyId(history.state.details.company)
			}
			if (history.state.details.userType) {
				this.handleSystemType(history.state.details.userType);
			}
			this.retrieveUnitsByPlantId(history.state.details.plant)
			history.replaceState({ details: history.state.details }, '')
		} else {
			if (this.currentRole === Roles.admin) {
				this.assignedPlants.map((assignment: Plant) =>
					this.plants.push(assignment),
				)
			}
		}

		// generate super admins on load
		const pageNumber = this.currentPage
		const pageSize = this.pageSize

		const item = {
			pageSize,
			pageNumber,
			search: this.searchInput,
		}

		if (
			(this.selectedPlant.plantId === 0 &&
				this.selectedCompany.companyId === 0 &&
				this.currentRole === Roles.superAdmin) ||
			(this.selectedPlant.plantId === 0 && this.currentRole === Roles.admin)
		) {
			this.userService.retrieveUsers(item).subscribe({
				next: (response: any) => {
					this.user = response.item1
					this.usersToDisplay = this.filterUsersByRole(this.user)
					this.totalPages = response.item2
					this.loopArray = new Array(this.totalPages)
						.fill(0)
						.map((_, index) => index + 1)
				},
				error: (response) => {
					this.errorMessage =
						this.errorService.handleRetrievalErrors(response)
				},
			})
		}
	}

	ngOnDestroy(): void {
		this.persistRefreshState()
	}

	private persistRefreshState(): void {
		this.refreshState.save('settings:users', {
			selectedCompany: this.selectedCompany,
			selectedPlant: this.selectedPlant,
			selectedUserType: this.selectedUserType,
			selectedUserRole: this.selectedUserRole,
			searchInput: this.searchInput,
			currentPage: this.currentPage,
		})
	}

	selectedEmail: string = ''
	handleDeactivate(role: string) {
		if (role != 'Temporary User') {
			this.authService.deactivateUser(this.selectedEmail).subscribe({
				next: (res) => {
					this.retrieveUnitsByPlantId(this.selectedPlant, true)
					this.handleSuccessToast('User-Deactivated')
				},
				error: (error) => {
					this.errorService.handleError(
						error,
						'Error deactivating this user',
					)
				},
			})
		} else {
			this.authService.deactivateTempUser(this.selectedEmail).subscribe({
				next: (res) => {
					this.retrieveUnitsByPlantId(this.selectedPlant, true)
					this.handleSuccessToast('Temp-User-Deactivated')
				},
				error: (error) => {
					this.errorService.handleError(
						error,
						'Error deactivating this temporary user',
					)
				}
			})
		}
	}
	showDeactivateMenu = false
	showDeactivateModal(
		firstName: string,
		lastName: string,
		role: string,
		email: string,
	) {
		if (this.currentRole === Roles.admin && role === Roles.superAdmin) {
			return
		}
		this.showMenu = false
		this.selectedEmail = email
		this.deactivateText.message1 =
			'You are about to deactivate ' +
			role +
			' ' +
			firstName +
			' ' +
			lastName +
			' in Smart Forms. This person will lose access to any part of the Smart Verra they previously had access to.'
		this.deactivateText.title = 'Deactivate ' + firstName + ' ' + lastName
		this.showDeactivateMenu = true
		this.deactivationRole = role;
	}

	emptyStateCompanies = {
		imgPath: '../../assets/user-icon.svg',
		message1: 'Select an Account Holder to see associated users listed here',
		message2: '',
	}

	emptyStatePlants = {
		imgPath: '../../assets/user-icon.svg',
		message1: 'Select a Client to see associated users listed here',
		message2: '',
	}

	emptyStateDataPoints = {
		imgPath: '../../assets/user-icon.svg',
		message1:
			'There are no Admins & Non-admins associated with this Company and Plant',
		message2: 'Created user will appear here.',
	}

	deactivateText = {
		title: '',
		message1: '',
		message2: 'This action is irreversible. Do you wish to proceed?',
	}
}
